/**
 * 친구 초대 섹션 — 자주 함께한 사람 추천 + 초대코드/공유/지난모임에서 초대.
 *
 * - 예정 모임 화면 (MeetingUpcomingView) 과 진행 중 모임 (MeetingDetail inline view) 양쪽에서 재사용.
 * - 바깥 여백은 호출자가 정합니다 (withSectionPadding).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { copyToClipboard } from 'common/utils/share';
import { MeetingRepository } from 'repository/meetingRepository';
import { markOnboardingCompleted } from 'common/utils/onboarding';
import { Avatar, Button, CopyRow, Section, SectionHeader, SvgBase } from 'components/ds';
import styles from './MeetingInviteSection.module.css';

const IconShare = (p: any) => (
    <SvgBase {...p}><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" /></SvgBase>
);
const IconLink = (p: any) => (
    <SvgBase {...p}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></SvgBase>
);
const IconHistory = (p: any) => (
    <SvgBase {...p}><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l4 2" /></SvgBase>
);
const IconCheck = (p: any) => (
    <SvgBase {...p}><polyline points="20 6 9 17 4 12" /></SvgBase>
);
const IconUserPlus = (p: any) => (
    <SvgBase {...p}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="8.5" cy="7" r="4" />
        <line x1="20" y1="8" x2="20" y2="14" />
        <line x1="23" y1="11" x2="17" y2="11" />
    </SvgBase>
);

interface Props {
    meetingId: string;
    inviteCode?: string;
    onShare: () => void;
    onOpenInviteFromPast: () => void;
    /** 외부 padding 컨트롤 — 카드 내부에 끼울지(false) 또는 자체 section 패딩(true)을 줄지. 기본 true. */
    withSectionPadding?: boolean;
    /** "함께하면 좋을 사람" 헤더를 노출할지 — false 면 카드 안에 묻혀서 헤더 없이 컴팩트하게. 기본 true. */
    showHeaders?: boolean;
    /**
     * 외부(예: InviteFromPastMeetings 모달)에서 초대 완료한 userNo 목록.
     * 이 컴포넌트의 frequent co-members 항목 중 일치하는 행은 "초대됨" 으로 표시되도록 내부 set 에 merge.
     */
    externallyInvitedUserNos?: number[];
}

const CO_MEMBERS_PREVIEW_LIMIT = 3;

export const MeetingInviteSection: React.FC<Props> = ({
    meetingId,
    inviteCode,
    onShare,
    onOpenInviteFromPast,
    withSectionPadding = true,
    showHeaders = true,
    externallyInvitedUserNos,
}) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();

    const [coMembers, setCoMembers] = useState<Array<{ userNo: number; nickname: string; sharedCount: number; lastSharedAt?: string }>>([]);
    const [coMembersLoading, setCoMembersLoading] = useState(false);
    const [coMembersExpanded, setCoMembersExpanded] = useState(false);
    const [invitedUserNos, setInvitedUserNos] = useState<Set<number>>(new Set());
    const [invitingUserNo, setInvitingUserNo] = useState<number | null>(null);

    // 외부에서 초대 완료된 userNo 들을 내부 set 에 merge — 행마다 "초대됨" 으로 표시
    useEffect(() => {
        if (!externallyInvitedUserNos || externallyInvitedUserNos.length === 0) return;
        setInvitedUserNos(prev => {
            const next = new Set(prev);
            externallyInvitedUserNos.forEach(n => next.add(n));
            return next;
        });
    }, [externallyInvitedUserNos]);

    useEffect(() => {
        if (!meetingId) return;
        let cancelled = false;
        setCoMembersLoading(true);
        MeetingRepository.onGetFrequentCoMembers(meetingId, 20)
            .then((res: any) => {
                if (cancelled) return;
                const list = res?.data?.data || res?.data || [];
                setCoMembers(Array.isArray(list) ? list.map((r: any) => ({
                    userNo: Number(r.userNo),
                    nickname: (() => {
                        const raw = r.nickname || '';
                        try { return raw ? decodeURIComponent(raw) : `유저${r.userNo}`; } catch { return raw || `유저${r.userNo}`; }
                    })(),
                    sharedCount: Number(r.sharedCount || 0),
                    lastSharedAt: r.lastSharedAt,
                })) : []);
            })
            .catch(() => { if (!cancelled) setCoMembers([]); })
            .finally(() => { if (!cancelled) setCoMembersLoading(false); });
        return () => { cancelled = true; };
    }, [meetingId]);

    const handleInviteCoMember = async (userNo: number) => {
        if (!meetingId || invitingUserNo) return;
        setInvitingUserNo(userNo);
        try {
            await MeetingRepository.onPostInviteToMeeting(meetingId, { inviteeUserNos: [userNo] });
            setInvitedUserNos(prev => new Set(prev).add(userNo));
            markOnboardingCompleted('step_invite');
            showAlert({ message: t('meetingInvite.alertInviteSent') });
        } catch {
            showAlert({ message: t('meetingInvite.alertInviteFail') });
        } finally {
            setInvitingUserNo(null);
        }
    };

    const inviteLink = `${window.location.origin}/invitation/${inviteCode || ''}`;

    /* 바깥 여백은 호출자가 정합니다.
       withSectionPadding=true 면 스스로 Section 을 두르고, false 면 내용만 냅니다. */
    const wrap = (key: string, children: React.ReactNode) => (
        withSectionPadding
            ? <Section key={key} divided>{children}</Section>
            : <div key={key} className={styles.plain}>{children}</div>
    );

    return (
        <>
            {/* 자주 함께한 사람 */}
            {(coMembersLoading || coMembers.length > 0) && wrap('co', (
                <>
                    {showHeaders && (
                        <SectionHeader title={t('meetingInvite.coTitle')} sub={t('meetingInvite.coDesc')} />
                    )}
                    {coMembersLoading ? (
                        <p className={styles.state}>{t('meetingInvite.loadingRec')}</p>
                    ) : (
                        <>
                            <ul className={styles.list}>
                                {(coMembersExpanded ? coMembers : coMembers.slice(0, CO_MEMBERS_PREVIEW_LIMIT)).map(m => {
                                    const invited = invitedUserNos.has(m.userNo);
                                    const inviting = invitingUserNo === m.userNo;
                                    return (
                                        <li key={m.userNo} className={styles.row}>
                                            <Avatar size="sm" person={{ userNo: m.userNo, name: m.nickname || '?' }} />
                                            <span className={styles.rowBody}>
                                                <span className={styles.rowName}>{m.nickname}</span>
                                                <span className={styles.rowMeta}>
                                                    {t('meetingInvite.sharedCount', { count: m.sharedCount })}
                                                </span>
                                            </span>
                                            <Button
                                                size="sm"
                                                variant={invited ? 'ghost' : 'secondary'}
                                                disabled={invited || inviting}
                                                onClick={() => handleInviteCoMember(m.userNo)}
                                                icon={invited ? <IconCheck size={13} /> : <IconUserPlus size={13} />}
                                            >
                                                {invited ? t('meetingInvite.invited')
                                                    : inviting ? t('meetingInvite.inviting')
                                                        : t('meetingInvite.invite')}
                                            </Button>
                                        </li>
                                    );
                                })}
                            </ul>

                            {coMembers.length > CO_MEMBERS_PREVIEW_LIMIT && (
                                <Button variant="ghost" block onClick={() => setCoMembersExpanded(v => !v)}>
                                    {coMembersExpanded
                                        ? t('meetingInvite.collapse')
                                        : t('meetingInvite.moreCount', { count: coMembers.length - CO_MEMBERS_PREVIEW_LIMIT })}
                                </Button>
                            )}
                        </>
                    )}
                </>
            ))}

            {/* 친구 초대 — 코드 · 공유 · 지난 모임 · 링크 */}
            {wrap('invite', (
                <>
                    {showHeaders && <SectionHeader title={t('meetingInvite.friendInvite')} />}
                    {inviteCode && (
                        <CopyRow
                            label={t('meetingInvite.inviteCode')}
                            value={inviteCode}
                            copyLabel={t('meetingInvite.copy')}
                            onCopy={async () => {
                                await copyToClipboard(inviteCode);
                                markOnboardingCompleted('step_invite');
                                showAlert({ message: t('meetingInvite.alertCodeCopied') });
                            }}
                        />
                    )}
                    <div className={styles.actions}>
                        <Button block icon={<IconShare size={16} />} onClick={onShare}>
                            {t('meetingInvite.shareKakaoLink')}
                        </Button>
                        <Button variant="secondary" block icon={<IconHistory size={16} />} onClick={onOpenInviteFromPast}>
                            {t('meetingInvite.inviteFromPast')}
                        </Button>
                        <Button
                            variant="ghost"
                            block
                            icon={<IconLink size={14} />}
                            onClick={async () => {
                                await copyToClipboard(inviteLink);
                                markOnboardingCompleted('step_invite');
                                showAlert({ message: t('meetingInvite.alertLinkCopied') });
                            }}
                        >
                            {t('meetingInvite.copyLinkOnly')}
                        </Button>
                    </div>
                </>
            ))}
        </>
    );
};
